import type { PiniaPluginContext, StateTree } from 'pinia'
import { logError } from '@/utils/safeErrorLog'

/**
 * Persist Pinia stores exactly the way the React portal's zustand `persist` middleware did, so a
 * patient's stored session and intake progress survive the switch to Vue, and a rollback to React.
 *
 * - Format: zustand (v5, `createJSONStorage`) stores `JSON.stringify({ state, version })`, where
 *   `state` is the `partialize`d state. On load it keeps the stored state only when the stored
 *   version equals the store's version, or after `migrate` upgrades it; otherwise the defaults.
 * - Timing: zustand writes inside `set()`, so storage is current the moment an action returns.
 *   The guards and `helper.getProfile()` read `portal-profile` straight from storage, so writes
 *   here are synchronous too (`flush: 'sync'`), not on the next tick.
 * - Hydration: zustand merges the stored state over the defaults at the top level only
 *   (`{ ...current, ...persisted }`); a stored nested object replaces the default whole.
 *
 * Use it through the store option: `defineStore(id, setup, { zustandPersist: { key: '...' } })`,
 * with `zustandPersistPlugin` installed on the Pinia instance (main.ts).
 */

interface ZustandPersistOptions {
  /** zustand `persist` `name`: the storage key. */
  key: string
  /** Defaults to localStorage, as zustand's does. */
  storage?: Pick<Storage, 'getItem' | 'setItem'>
  /** zustand `partialize` that picks top-level fields. Omit to persist the whole state. */
  paths?: string[]
  /** zustand `persist` `version` (zustand's default is 0). */
  version?: number
  /**
   * zustand `partialize`, for shapes `paths` can't express (e.g. "all state, but these fields reset").
   * It receives the store state (already narrowed by `paths`, when both are given).
   */
  partialize?: (state: StateTree) => StateTree
  /** zustand `migrate`: turn state saved at `fromVersion` into the current shape. */
  migrate?: (persistedState: unknown, fromVersion: number) => StateTree
}

declare module 'pinia' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  export interface DefineStoreOptionsBase<S extends StateTree, Store> {
    /** Persist this store in the React portal's zustand format (see zustandCompat.ts). */
    zustandPersist?: ZustandPersistOptions
  }
}

/** zustand's on-disk envelope. */
interface ZustandEnvelope {
  state: StateTree
  version?: number
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isEnvelope(
  value: Record<string, unknown>
): value is Record<string, unknown> & ZustandEnvelope {
  return (
    isPlainObject(value.state) && (value.version === undefined || typeof value.version === 'number')
  )
}

type SerializerOptions = Pick<ZustandPersistOptions, 'version' | 'partialize' | 'migrate'>

/** Writes zustand's `{ state, version }` envelope (after `partialize`). */
export function serializeZustand(state: StateTree, options: SerializerOptions = {}): string {
  const persisted = options.partialize ? options.partialize(state) : state
  return JSON.stringify({ state: persisted, version: options.version ?? 0 })
}

/**
 * Reads a stored value back into the state to apply: the envelope's `state` (a bare object with no
 * envelope is accepted as-is), migrated when the version differs. `{}` keeps the defaults.
 */
export function deserializeZustand(raw: string, options: SerializerOptions = {}): StateTree {
  const version = options.version ?? 0
  const parsed: unknown = JSON.parse(raw)
  if (!isPlainObject(parsed)) return {}
  if (!isEnvelope(parsed)) return parsed

  const stored = parsed.version
  if (typeof stored === 'number' && stored !== version) {
    if (options.migrate) return options.migrate(parsed.state, stored)
    console.error(
      `[persist] stored state is version ${stored}, the store is version ${version}, and there is no migrate; using defaults`
    )
    return {}
  }
  return parsed.state
}

function pickTopLevel(state: StateTree, paths: string[] | undefined): StateTree {
  if (!paths) return state
  const picked: StateTree = {}
  for (const path of paths) if (path in state) picked[path] = state[path]
  return picked
}

/**
 * Pinia plugin: for every store with a `zustandPersist` option, hydrate it from storage when it is
 * created (top-level replace), then write it back synchronously on every state change.
 */
export function zustandPersistPlugin({ options, store, pinia }: PiniaPluginContext): void {
  const persist = options.zustandPersist
  if (!persist) return
  // HMR builds throwaway `__hot:` stores that are not registered in pinia.state; skip them so a
  // hot update does not add a second writer for the same storage key.
  if (!(store.$id in pinia.state.value)) return
  let storage: Pick<Storage, 'getItem' | 'setItem'>
  try {
    storage = persist.storage ?? window.localStorage
  } catch {
    return // storage blocked (private mode): run in memory, as zustand does
  }

  try {
    const raw = storage.getItem(persist.key)
    if (raw) {
      const stored = deserializeZustand(raw, persist)
      // Top-level replace, as zustand's merge: `$patch(object)` would deep-merge nested objects.
      store.$patch((state) => {
        Object.assign(state, stored)
      })
    }
  } catch (e) {
    logError(`[persist] could not read ${persist.key}`, e)
  }

  store.$subscribe(
    (_mutation, state) => {
      try {
        storage.setItem(persist.key, serializeZustand(pickTopLevel(state, persist.paths), persist))
      } catch (e) {
        logError(`[persist] could not write ${persist.key}`, e)
      }
    },
    { detached: true, flush: 'sync' }
  )
}
